<template>
    <div>
        <header v-if="currentStep === 1" class="header header--fixed">
            <div class="container">
                <div class="row align-items--center">
                    <div class="col-2 offset-10 text-right">
                        <router-link class="btn btn--icon btn--white" to="/" exact>
                            <i class="material-icons">close</i>
                        </router-link>
                    </div>
                </div>
            </div>
        </header>

        <loading v-if="currentStep === 1">
            Searching for opponent...
        </loading>

        <div v-if="currentStep === 2">
            <loading>
                Loading<template v-slot:subtitle>Found an opponent: {{ opponentName }}</template>
            </loading>
        </div>

        <div v-if="currentStep == 3">

            <section class="fullscreen game">
                <div class="container container-lg game__inner">
                    <div class="game__header mt--10">
                        <div class="game__info mb--md">
                            <div :class="['game__player', 'game__player--first', 'animated', 'bounceInLeft', preferences.profileColor, {'game__player--active': answered }]">
                                <div class="game__player__inner">
                                    <div class="game__player__avatar">
                                        <img :src="avatar" alt="" />
                                        <span class="active"></span>
                                    </div>

                                    <div>
                                        <div class="game__player__name game__player__name--max-width">{{ nick }}</div>

                                        <div class="game__player__score">{{ points }}</div>
                                    </div>

                                </div>

                                <i class="material-icons" v-if="answered">check</i>
                            </div>

                            <div class="game__progress animated bounceInDown">
                                <div class="game__progress__title">Level {{ level }}</div>

                                <div :class="['game__progress__time', {'game__progress__time--active': timerActive}]" v-show="beforeGameCounterReady">
                                    <countdown @hook:mounted="countdownReady = true" ref="countdownTimer"
                                               :speed="100" :left-time="20000" @finish="() => handleCountdownEnd()"
                                               @process="(vac) => timeLeft(vac)">
                                        <div id="countdown-number" slot-scope="{ timeObj }"
                                             class="game__progress__time game__progress__time--active">
                                            <span v-if="`${timeObj.leftTime}` < 10">0</span>
                                            <span v-else>{{ leftTimeSec }}</span>
                                            <svg>
                                                <circle r="16" cx="19" cy="19" v-bind:style="timerStyle"></circle>
                                            </svg>
                                        </div>

                                    </countdown>
                                </div>

                                <div class="game__progress__step">{{ questionIndex }}/7</div>
                            </div>

                            <div :class="['game__player', 'game__player--last', 'animated', 'bounceInRight', opponentColor, {'game__player--active': opponentAnswered }]">
                                <div class="game__player__inner">
                                    <div class="game__player__avatar">
                                        <img src="/img/pictures/avatar-1.jpg" alt=""/>
                                        <span class="active"></span>
                                    </div>

                                    <div>
                                        <div class="game__player__name game__player__name--max-width">
                                            {{ opponentName }}
                                        </div>

                                        <div class="game__player__score">{{ opponentPoints }}</div>
                                    </div>
                                </div>

                                <i class="material-icons" v-if="opponentAnswered">check</i>
                            </div>
                        </div>
                    </div>

                    <div class="game__body panel bg--layer-light text--center mb--md animated bounceInUp" v-if="!beforeGameCounterReady">
                        <div class="game__body__inner">
                            <div class="game__question">
                                <div v-if="(beforeGameCounterValue -1) > 0" class="game__success game__success--animate">
                                    <strong>{{ beforeGameCounterValue - 1 }}</strong>
                                </div>
                                <div class="game__success game__success--animate" v-else><strong>GO!</strong></div>
                            </div>
                        </div>
                    </div>

                    <motivation-messages :good-answers-in-a-row="goodAnswersInARow" :show-message="showMessage"></motivation-messages>

                    <div class="game__body panel bg--layer-light text--center mb--md" v-if="beforeGameCounterReady && !showMessage">
                        <div class="game__body__inner">
                            <h4 class="text--xs opacity--md mb--sm">{{ question.group }}</h4>

                            <div :class="{'game__question': true, 'game__question--long': (question && question.question.length > 40) }">{{ question.question }}</div>
                        </div>
                    </div>

                    <form v-if="beforeGameCounterReady" class="row game__footer mb--0">

                        <div class="col-12 col-lg-6" v-for="answer in question.answers">
                            <button type="button" @click="sendAnswer(answer.id)"
                                    :class="{'btn': true, 'btn--action': true, 'mb--sm': true, 'btn--white': (!answered || (answered !== answer.id && answer.id !== correctAnswer)), 'btn--green': answered && answer.id === correctAnswer, 'btn--select': answered && answered == answer.id && answer.id !== correctAnswer}"
                                    :disabled="answered && answered !== answer.id && answer.id !== correctAnswer">
                                {{ answer.answer }}
                                <div class="btn--action__icon btn--action__icon--right"
                                     v-if="opponentAnswered && answered && opponentAnswered == answer.id">
                                    <img src="/img/pictures/avatar-1.jpg" alt=""/>
                                </div>

                                <div class="btn--action__icon btn--action__icon--left"
                                     v-if="answered && answered == answer.id"
                                     :disabled="answered && answered == answer.id && answer.id !== correctAnswer">
                                    <img :src="avatar" alt="" />
                                </div>
                            </button>
                        </div>

                    </form>
                </div>
            </section>
        </div>

        <div v-if="currentStep == 4">

            <game-end
                :you-won="youWon"
                :home-player="nick"
                :home-player-points="points"
                :home-player-xp="homePlayerXp"
                :home-player-avatar="avatar"
                :away-player="opponentName"
                :away-player-points="opponentPoints"
                :away-player-avatar="'/img/pictures/avatar-1.jpg'"
                :speed-bonus="speedBonus"
                :perfetct-game-bonus="perfectGameBonus"
                :quiz-id="quiz.id"
            ></game-end>
        </div>

        <div v-if="currentStep == 5">
            <loading>
                Something went wrong.
                <template v-slot:content>
                    <a href="/" class="btn btn--block btn--secondary">Go to dashboard</a>
                </template>
            </loading>
        </div>
    </div>
</template>

<script>
    import api from '../../../api/againstcomputer';
    import { mapState, mapGetters } from 'vuex'
    import MotivationMessages from "../partial/MotivationMessages";
    import ScoreTable from "../partial/ScoreTable";

    export default {
        components: {ScoreTable, MotivationMessages},
        data: function () {
            return {
                currentStep: 1,
                opponentName: null,
                opponentColor: 'blue',
                channel: null,
                question: null,
                quiz: null,
                opponentAnswered: null,
                opponentAnswer: null,
                results: null,
                answered: false,
                nick: '',
                leftTime: 20000,
                leftTimeSec: 20,
                points: 0,
                opponentPoints: 0,
                questionIndex: 0,
                totalPoints: null,
                timerActive: true,
                goodAnswersInARow: 0,
                showMessage: false,
                countdownReady: false,
                correctAnswer: null,
                beforeGameCounterReady: false,
                beforeGameCounterValue: 4,
                answerResponse: null
            }
        },

        methods: {
            timeLeft(props) {
                this.leftTime = props.timeObj.leftTime
                this.leftTimeSec = props.timeObj.ceil.s

                if (! this.opponentAnswered && (20000 - this.leftTime) >= this.opponentAnswer.time && this.beforeGameCounterReady) {
                    this.opponentAnswered = true
                }
            },

            handleCountdownEnd() {

                this.timerActive = false
                if (this.answered) {
                    return;
                }

                this.answered = -1
                this.goodAnswersInARow = 0

                api.answer({
                    answer: null,
                    question: this.question,
                    quiz: this.quiz,
                    leftTime: 0
                }).then((response) => {
                    this.answerResponse = response.data
                }).catch( (error) => this.currentStep = 5)
            },

            showAnswerResult() {
                if (this.answerResponse.isSuccess) {
                    ++this.goodAnswersInARow
                    this.showMessage = true
                } else {
                    this.goodAnswersInARow = 0
                }

                this.correctAnswer = this.answerResponse.correctAnswer
                this.points += this.answerResponse.points
                this.opponentPoints += this.answerResponse.opponentPoints
                this.opponentAnswered = this.answerResponse.opponentAnswer

                this.sleep(3000).then(() => {
                    api.getQuestion(this.quiz).then((response) => {

                        if (get(response.data, 'gameClosed')) {
                            this.points = response.data.points
                            this.opponentPoints = response.data.opponentPoints
                            this.results = response.data.results
                            this.currentStep = 4
                            this.totalPoints = response.data.totalPoints
                            this.question = null

                            return;
                        }

                        this.showMessage = false
                        ++this.questionIndex
                        this.answerIsCorrect = null
                        this.answerResponse = null
                        this.answered = null
                        this.opponentAnswer = null
                        this.opponentAnswered = null
                        this.question = response.data.data
                        this.opponentAnswer = response.data.extra
                    })

                    this.startTimer()
                })
            },

            sendAnswer(answerId) {
                if (this.answered) {
                    return;
                }

                this.timerActive = false
                this.answered = answerId

                api.answer({
                    answer: answerId,
                    question: this.question,
                    quiz: this.quiz,
                    leftTime: this.leftTime
                }).then((response) => {
                    this.answerResponse = response.data
                }).catch((error) => this.currentStep = 5)
            },

            async createGame() {
                const response = await api.create()
                this.quiz = response.data.data
            },

            startTimer() {
                this.leftTime = 20 * 1000
                if (this.countdownReady && this.$refs.countdownTimer !== undefined) {
                    this.$refs.countdownTimer.startCountdown(true)
                }
            },

            start() {
                this.timerActive = true

                this.createGame().then(() => {
                    this.opponentName = get(collect(this.quiz.quizUsers).where('user_id', null).first(), 'nick')
                    this.currentStep = 2

                    setTimeout(() => {
                        api.getQuestion(this.quiz).then((res) => {
                            this.startTimer()
                            ++this.questionIndex
                            this.question = res.data.data
                            this.opponentAnswer = res.data.extra

                            this.currentStep = 3

                            if (! this.beforeGameCounterReady) {
                                let counter = setInterval(() => {
                                    if (this.beforeGameCounterValue > 0) {
                                        --this.beforeGameCounterValue
                                    }

                                    if (this.beforeGameCounterValue === 0) {
                                        clearInterval(counter)
                                        this.beforeGameCounterReady = true
                                        this.question = res.data.data
                                        this.startTimer()
                                    }
                                }, 1000)
                            }
                        }).catch(() => {
                            this.currentStep = 5
                        })
                    }, 3000);
                })
            },
        },

        computed: {
            ...mapState('auth', [
                'user'
            ]),
            ...mapGetters('auth', [
                'avatar',
                'preferences',
            ]),

            timerStyle() {
                let strokeLength = 16 * Math.PI * 2;

                if (this.leftTime < 5000) {
                    return {
                        stroke: '#D95656',
                        strokeDashoffset: strokeLength - (strokeLength / 20 * (Math.ceil(this.leftTime / 1000))) + 'px',
                        strokeDasharray: strokeLength + 'px',
                    }
                }

                return {
                    strokeDashoffset: strokeLength - (strokeLength / 20 * (Math.ceil(this.leftTime / 1000))) + 'px',
                    strokeDasharray: strokeLength + 'px',
                }
            },

            homePlayerWon() {
                if (this.totalPoints === null) {
                    return false;
                }

                if (! get(this.totalPoints, [this.nick, 'total_points'], null) || ! get(this.totalPoints, [this.opponentName, 'total_points'], null)) {
                    return false;
                }

                return get(this.totalPoints, [this.nick, 'total_points'], 0) > get(this.totalPoints, [this.opponentName, 'total_points'], 0)
            },

            awayPlayerWon() {
                if (this.totalPoints === null) {
                    return false;
                }

                if (! get(this.totalPoints, [this.nick, 'total_points'], null) || ! get(this.totalPoints, [this.opponentName, 'total_points'], null)) {
                    return false;
                }

                return get(this.totalPoints, [this.nick, 'total_points'], 0) < get(this.totalPoints, [this.opponentName, 'total_points'], 0)
            },

            perfectGameBonus() {
                return get(this.totalPoints, [this.nick, 'perfectGame'])
            },

            speedBonus() {
                return get(this.totalPoints, [this.nick, 'speedBonus'])
            },

            homePlayerXp() {
                return get(this.totalPoints, [this.nick, 'xp'], 0)
            },

            youWon() {
                if (get(this.totalPoints, [this.nick, 'total_points'], 0) > get(this.totalPoints, [this.opponentName, 'total_points'], 0)) {
                    return true
                }

                return false
            }
        },

        created() {
            this.nick = this.user.nick
            this.level = this.user.level
            this.opponentColor = this.preferences.profileColor === 'blue' ? 'pink' : 'blue'
            this.start()
        },

        watch: {
            answered: function (newValue, oldValue) {
                if (!oldValue && newValue && this.answerResponse && this.opponentAnswered) {
                    this.showAnswerResult()
                }
            },

            opponentAnswered: function (newValue, oldValue) {
                if (!oldValue && newValue && this.answerResponse && this.answered) {
                    this.showAnswerResult()
                }
            },

            answerResponse: function (newValue, oldValue) {
                if (!oldValue && newValue && this.opponentAnswered && this.answered) {
                    this.showAnswerResult()
                }
            },

            currentStep: function (step) {
                if (step === 4) {
                    $('html').removeClass('mobile-fixed-screen')
                } else {
                    $('html').addClass('mobile-fixed-screen')
                }
            }
        }
    }
</script>
